import { normalizeHpoId } from '@/features/terminology/hpo/hpoLabelResolver'
import type { HPONode } from '@/features/terminology/hpo/hpoTypes'
import type { CategorizedHPOOption } from '@/types/RequisitionFormTypes'

/** Minimal shape of an Indications-modal phenotype (id/name/type). */
export interface IndicationPhenotype {
  id: string
  name: string
  type: string
}

const categoryForType = (type: string): CategorizedHPOOption['category'] =>
  type === 'key' ? 'Key' : type === 'additional' ? 'Additional' : 'Excluded'

/**
 * Map phenotypes saved from the Indications modal to canonical
 * `clinicalDiagnosis` items while preserving each term's original provenance.
 *
 * GEN-4598: the create flow previously stamped every saved phenotype
 * `source: 'manual'`, which silently opted auto-extracted terms out of the
 * post-submit drift / removed-term reconciliation (GEN-4571 / GEN-4583), since
 * that machinery exempts `source: 'manual'`. Here a term that already exists in
 * `priorTerms` keeps its recorded source (`extracted`, `phenoml`, `unknown`, or
 * an undefined legacy value); only a genuinely new term (no prior entry) is
 * tagged `manual`. Matching is on the normalized HPO id so `HP_x`/`HP:x`/bare
 * digit encodings collide.
 */
export function mapIndicationsPreservingProvenance(
  phenotypes: IndicationPhenotype[],
  priorTerms: CategorizedHPOOption[] = []
): CategorizedHPOOption[] {
  const priorSourceById = new Map(priorTerms.map((term) => [normalizeHpoId(term.id), term.source]))
  return phenotypes.map((term) => {
    const key = normalizeHpoId(term.id)
    const source = priorSourceById.has(key) ? priorSourceById.get(key) : 'manual'
    return {
      id: term.id,
      label: term.name,
      node: { id: term.id, lbl: term.name, type: 'PhenotypicFeature' } as HPONode,
      category: categoryForType(term.type),
      source,
    }
  })
}
